import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

// Actions
import { createCampaign } from "../../../actions/campaign/CampaignActions";
import { getRegionNames } from "../../../actions/regions/RegionActions";

// Constants and Utils
import { CampaignTypesConstants } from "../../../constants/GeneralConstants";
import { checkIsCoralMediaSpecific } from "../../../common-utils/user-utils/userUtils";

// Components
import Spinner from "../../../components/spinner/Spinner";
import MultiCitySelectionForm from "../../../mavin/components/multi-city-selection-form/MultiCitySelectionForm";
import { PlainDropdown } from "../../../components/dropdown/Dropdown";
import {
  PlanSetupAddCities,
  PlanSetupFooter,
  PlanSetupHeader,
  PlanSetupStartDateAndDuration,
  PlanSetupTargetGroupAndBudget,
  PlanSetupTitle,
} from "../../../components/campaign-plan-setup-utils/CampaignPlanSetupUtils";
import CampaignTargetGroupForm from "../../../components/campaign-tg-form/CampaignTargetGroupForm";

// CSS
import "../CampaignPlanning.scss";

function CampaignTypeSection({ campaignType, setCampaignType }) {
  return (
    <div className="py-3">
      <div className="col-6 pl-0">
        <h5>{"Campaign Type"}</h5>
        <PlainDropdown
          items={CampaignTypesConstants}
          className="btn shadow-none rounded-lg border px-3"
          placeHolder="Select Campaign Type"
          selectedId={campaignType}
          onItemSelect={setCampaignType}
          dropMenuClassName="dropdown-fixed-height"
          isFirstItemSelectable={true}
        />
      </div>
    </div>
  );
}

/**
 * Page
 */
function CampaignPlanSetupPage() {
  const dispatch = useDispatch();
  const history = useHistory();

  // State
  const [title, setTitle] = useState("");
  const [infoByCity, setInfoByCity] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [campaignType, setCampaignType] = useState("");

  // Tg Form Related State
  const [isTargetGroupEnabled, setIsTargetGroupEnabled] = useState(false);
  const [selectedTgAttrs, setSelectedTgAttrs] = useState([]);
  const [selectedPoiTypes, setSelectedPoiTypes] = useState([]);

  // Selector State
  const regionsName = useSelector((state) => state.region.regionsName) || [];
  const regionsNameLoading = useSelector((state) => state.region.namesLoading);

  // for "MULTIPLE-City-Selection-form"
  const openMultiCitySelectionForm = useSelector(
    (state) => state.multiCitySelectionModal.openModal
  );

  // Dispatch
  useEffect(() => {
    dispatch(getRegionNames());
  }, [dispatch]);

  // coral media
  const isCoralMediaSpecific = checkIsCoralMediaSpecific();

  // Create Campaign Submit Button
  function handleSubmit() {
    const newCampaign = { infoByCity, title, startDate, endDate, campaignType };

    // adding tg selected attributes and poi types to new campaign if tg is enabled
    if (isTargetGroupEnabled) {
      newCampaign["tgAttributesIds"] = selectedTgAttrs;
      newCampaign["poiTypeIds"] = selectedPoiTypes;
    }

    // create campaign
    dispatch(createCampaign(newCampaign, history));
  }

  // campaign plan setup button
  const isPlanCampaignBtnDisabled =
    !title || (startDate && !endDate) || infoByCity.length == 0;

  // Loading
  if (regionsNameLoading) {
    return <Spinner className="spinner-center mt-2" />;
  }

  return (
    <div className="content-wrapper">
      {/* Page- header */}
      <PlanSetupHeader />

      <div className="page-content">
        <div className="row">
          <div className="col-6">
            {/* Campaign-title */}
            <PlanSetupTitle title={title} setTitle={setTitle} />

            {/* Campaign Type */}
            {isCoralMediaSpecific && (
              <CampaignTypeSection
                campaignType={campaignType}
                setCampaignType={setCampaignType}
              />
            )}

            {/* StartDate and Duration */}
            <PlanSetupStartDateAndDuration
              startDate={startDate}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
            />

            {/* TG selection */}
            <PlanSetupTargetGroupAndBudget
              isTargetGroupEnabled={isTargetGroupEnabled}
              setIsTargetGroupEnabled={setIsTargetGroupEnabled}
            />

            {/* Selected Cities Container */}
            <PlanSetupAddCities
              infoByCity={infoByCity}
              setInfoByCity={setInfoByCity}
            />

            {/* Page-Footer Section */}
            <PlanSetupFooter
              handleSubmit={handleSubmit}
              disable={isPlanCampaignBtnDisabled}
            />
          </div>

          {/* Campaign TG Form */}
          {isTargetGroupEnabled && (
            <CampaignTargetGroupForm
              selectedTgAttrs={selectedTgAttrs}
              setSelectedTgAttrs={setSelectedTgAttrs}
              selectedPoiTypes={selectedPoiTypes}
              setSelectedPoiTypes={setSelectedPoiTypes}
            />
          )}
        </div>
      </div>

      {/* MODALS */}
      {openMultiCitySelectionForm && (
        <MultiCitySelectionForm
          regionsName={regionsName}
          onSubmit={setInfoByCity}
          citiesList={infoByCity}
        />
      )}
    </div>
  );
}

export default CampaignPlanSetupPage;
